/**
 * Review Results Page
 *
 * Main page for viewing MHR eligibility review results with integrated document viewer.
 * Features:
 * - LimitationsModal shown first (required acknowledgment)
 * - Split layout: results panel (left 40%) + document viewer (right 60%) on desktop
 * - Mobile: tabs for "Results" vs "Document"
 * - Responsive breakpoint at md (768px)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  Download,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  DollarSign,
  User,
  ClipboardList,
  ListFilter,
  XCircle,
  AlertTriangle,
} from 'lucide-react';

// Components
import { LimitationsModal } from '../components/Limitations';
import { PDFViewer } from '../components/DocumentViewer/PDFViewer';
import { PageNavigation } from '../components/DocumentViewer/PageNavigation';
import { ZoomControls } from '../components/DocumentViewer/ZoomControls';
import { ExcerptPanel } from '../components/DocumentViewer/ExcerptPanel';
import { OverallResultBanner } from '../components/Results/OverallResultBanner';
import { CriteriaResultCard } from '../components/Results/CriteriaResultCard';
import { CriterionVerification } from '../components/HumanReview/CriterionVerification';
import { ReviewSignOff } from '../components/HumanReview/ReviewSignOff';
import { Breadcrumb } from '../components/Breadcrumb';
import { Toast } from '../components/Toast';

// Hooks and API
import { useReview } from '../hooks/useReview';
import * as mhrApi from '../lib/api';

// Types
import type { Citation, CriterionResult, VerificationPayload } from '../types';

// =====================================================
// Helper Components
// =====================================================

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Header skeleton */}
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        <div className="flex-1">
          <div className="h-6 w-48 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
          <div className="h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
      </div>

      {/* Banner skeleton */}
      <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg" />

      {/* Content skeleton */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="h-8 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg" />
          <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        </div>
        <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded-lg" />
      </div>
    </div>
  );
}

function ErrorState({
  error,
  onRetry,
  onBack,
}: {
  error: string;
  onRetry: () => void;
  onBack: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="text-center max-w-md">
        <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Unable to Load Review</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors min-h-[44px] sm:min-h-0"
          >
            <ArrowLeft size={18} />
            Back to Dashboard
          </button>
          <button
            onClick={onRetry}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors min-h-[44px] sm:min-h-0"
          >
            <RefreshCw size={18} />
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}

function CollapsibleSection({
  title,
  icon: Icon,
  count,
  children,
  defaultOpen = true,
  iconBgColor = 'bg-gray-100 dark:bg-gray-700',
  iconColor = 'text-gray-600 dark:text-gray-400',
  priority = false,
}: {
  title: string;
  icon: React.ElementType;
  count: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
  iconBgColor?: string;
  iconColor?: string;
  priority?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-xl border overflow-hidden ${
        priority ? 'border-red-300 dark:border-red-700 ring-2 ring-red-100' : 'border-gray-200 dark:border-gray-700'
      }`}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${
          priority ? 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30' : 'bg-gray-50 dark:bg-gray-900/40 hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${iconBgColor}`}>
            <Icon className={`w-5 h-5 ${iconColor}`} />
          </div>
          <span className="font-semibold text-gray-900 dark:text-gray-100">{title}</span>
          <span className="text-sm text-gray-500 dark:text-gray-400">({count})</span>
          {priority && (
            <span className="px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-full">
              Review First
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronDown className="w-5 h-5 text-gray-400 dark:text-gray-500" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
        )}
      </button>
      {isOpen && <div className="p-4 space-y-4">{children}</div>}
    </div>
  );
}

function CancelConfirmationModal({
  isOpen,
  onConfirm,
  onCancel,
  isCancelling,
}: {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  isCancelling: boolean;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 p-2 bg-amber-100 dark:bg-amber-900/40 rounded-full">
            <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Cancel Review?
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Are you sure you want to cancel this review? Any progress made will be lost, and you will need to start a new review to analyze this patient's eligibility.
            </p>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isCancelling}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors min-h-[44px] sm:min-h-0"
          >
            Keep Reviewing
          </button>
          <button
            onClick={onConfirm}
            disabled={isCancelling}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors min-h-[44px] sm:min-h-0"
          >
            {isCancelling ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Cancelling...
              </>
            ) : (
              <>
                <XCircle size={16} />
                Cancel Review
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function CostSummary({
  totalCost,
  cacheSavings,
}: {
  totalCost: number;
  cacheSavings: number;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors min-h-[44px] sm:min-h-0"
      >
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <DollarSign size={16} />
          <span>Review Cost: ${totalCost.toFixed(4)}</span>
        </div>
        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {isOpen && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 text-sm">
          <div className="flex justify-between text-gray-600 dark:text-gray-400">
            <span>Analysis cost:</span>
            <span>${totalCost.toFixed(4)}</span>
          </div>
          {cacheSavings > 0 && (
            <div className="flex justify-between text-green-600 dark:text-green-400 mt-1">
              <span>Cache savings:</span>
              <span>-${cacheSavings.toFixed(4)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =====================================================
// Main Component
// =====================================================

export function ReviewResults() {
  const { reviewId } = useParams<{ reviewId: string }>();
  const navigate = useNavigate();

  // Review data and operations
  const { review, loading, error, isPolling, pollingError, retryPolling, verify, complete, cancel, exportReview, refetch } =
    useReview(reviewId);

  // Limitations modal state
  const [limitationsAcknowledged, setLimitationsAcknowledged] = useState(false);
  const [showLimitationsModal, setShowLimitationsModal] = useState(false);

  // Mobile tab state
  const [activeTab, setActiveTab] = useState<'results' | 'document'>('results');
  const [isMobile, setIsMobile] = useState(false);

  // Document viewer state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [zoom, setZoom] = useState(1.0);
  const [selectedCitation, setSelectedCitation] = useState<Citation | null>(null);

  // Expanded criteria state
  const [expandedCriteriaId, setExpandedCriteriaId] = useState<string | null>(null);

  // Export state
  const [isExporting, setIsExporting] = useState(false);

  // Cancel modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // Success notification state
  const [success, setSuccess] = useState<string | null>(null);
  const clearSuccess = useCallback(() => setSuccess(null), []);

  // Error notification state (e.g. a failed human verification must be visible,
  // never silently swallowed — Part 11 integrity)
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const clearError = useCallback(() => setErrorMsg(null), []);

  // Check limitations acknowledgment on mount
  useEffect(() => {
    if (reviewId) {
      const storageKey = `mhr-limitations-acknowledged-${reviewId}`;
      const acknowledged = localStorage.getItem(storageKey) === 'true';
      setLimitationsAcknowledged(acknowledged);
      if (!acknowledged) {
        setShowLimitationsModal(true);
      }
    }
  }, [reviewId]);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle limitations acknowledgment
  const handleLimitationsAcknowledge = useCallback(() => {
    if (reviewId) {
      const storageKey = `mhr-limitations-acknowledged-${reviewId}`;
      localStorage.setItem(storageKey, 'true');
      setLimitationsAcknowledged(true);
      setShowLimitationsModal(false);
    }
  }, [reviewId]);

  // Handle citation click - navigate to page and show excerpt
  const handleCitationClick = useCallback((citation: Citation) => {
    setSelectedCitation(citation);
    setCurrentPage(citation.page_number);
    if (isMobile) {
      setActiveTab('document');
    }
  }, [isMobile]);

  // Handle page load success
  const handleLoadSuccess = useCallback((numPages: number) => {
    setTotalPages(numPages);
  }, []);

  // Handle close excerpt panel
  const handleCloseExcerptPanel = useCallback(() => {
    setSelectedCitation(null);
  }, []);

  // Handle go to page from excerpt panel
  const handleGoToPage = useCallback((page: number) => {
    setCurrentPage(page);
    setSelectedCitation(null);
  }, []);

  // Handle verify criterion
  const handleVerify = useCallback(
    async (result: CriterionResult) => {
      setExpandedCriteriaId(result.id);
    },
    []
  );

  // Handle verification submit
  const handleVerificationSubmit = useCallback(
    async (resultId: string, payload: VerificationPayload) => {
      try {
        await verify(resultId, payload);
        setSuccess('Criterion verified successfully');
      } catch (err: any) {
        setErrorMsg(
          err?.response?.data?.error ||
            'Failed to save verification — the criterion was NOT recorded as verified. Please retry.'
        );
        // Re-throw so the child form/modal stays open on failure.
        throw err;
      }
    },
    [verify]
  );

  // Handle complete review
  const handleComplete = useCallback(async () => {
    await complete();
    setSuccess('Review completed and signed off');
  }, [complete]);

  // Handle export
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const result = await exportReview();
      // Open the export URL in a new tab
      window.open(result.url, '_blank');
      setSuccess('Report exported successfully');
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  }, [exportReview]);

  // Handle cancel review
  const handleCancelConfirm = useCallback(async () => {
    setIsCancelling(true);
    try {
      await cancel();
      setShowCancelModal(false);
    } catch (err) {
      console.error('Cancel failed:', err);
    } finally {
      setIsCancelling(false);
    }
  }, [cancel]);

  // For PDFViewer, fetch document as blob URL (proxied through backend to avoid S3 CORS)
  const [pdfUrl, setPdfUrl] = useState<string>('');
  useEffect(() => {
    if (!review?.document_id) return;
    let cancelled = false;
    // Track the URL created by THIS effect run and revoke exactly that one on
    // cleanup — reading pdfUrl from the closure would revoke a stale/empty URL
    // and leak the blob holding decrypted PHI PDF bytes.
    let createdUrl: string | null = null;
    mhrApi.getDocumentViewUrl(review.document_id)
      .then(url => {
        if (!cancelled) {
          setPdfUrl(url);
          createdUrl = url;
        } else if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      })
      .catch(err => console.error('[ReviewResults] Failed to load document:', err));
    return () => {
      cancelled = true;
      if (createdUrl && createdUrl.startsWith('blob:')) URL.revokeObjectURL(createdUrl);
    };
  }, [review?.document_id]);

  // Separate criteria by type
  const { exclusionCriteria, inclusionCriteria } = useMemo(() => {
    if (!review?.criteria_results) {
      return { exclusionCriteria: [], inclusionCriteria: [] };
    }

    return {
      exclusionCriteria: review.criteria_results.filter(c => c.type === 'exclusion'),
      inclusionCriteria: review.criteria_results.filter(c => c.type === 'inclusion'),
    };
  }, [review?.criteria_results]);

  // Count exclusions that are met (these are problems)
  const exclusionsMetCount = useMemo(() => {
    return exclusionCriteria.filter(c => c.result === 'met').length;
  }, [exclusionCriteria]);

  // Pages with findings for quick navigation
  const pagesWithFindings = useMemo(() => {
    if (!review?.document_coverage?.pages_with_findings) return [];
    return review.document_coverage.pages_with_findings;
  }, [review?.document_coverage?.pages_with_findings]);

  // Check if review is completed
  const isCompleted = review?.status === 'completed';

  // Check if review can be cancelled (only in-progress reviews)
  const isCancellable = review?.status && [
    'in_progress',
    'pass_1_exclusions',
    'pass_2_inclusions',
    'pass_3_verification',
  ].includes(review.status);

  // Check if review was cancelled
  const isCancelled = review?.status === 'cancelled';

  // Loading state
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <LoadingSkeleton />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <ErrorState
        error={error}
        onRetry={refetch}
        onBack={() => navigate('/mhr')}
      />
    );
  }

  // No review found
  if (!review) {
    return (
      <ErrorState
        error="Review not found"
        onRetry={refetch}
        onBack={() => navigate('/mhr')}
      />
    );
  }

  // Show limitations modal if not acknowledged
  if (!limitationsAcknowledged) {
    return (
      <LimitationsModal
        isOpen={showLimitationsModal}
        onAcknowledge={handleLimitationsAcknowledge}
        onClose={() => {}} // Cannot close without acknowledging
      />
    );
  }

  // Build breadcrumb items
  const breadcrumbItems = [
    { label: 'Dashboard', href: '/mhr' },
    { label: 'Review Results' },
  ];

  return (
    <div className="space-y-4">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Limitations modal (can be shown again if needed) */}
      <LimitationsModal
        isOpen={showLimitationsModal}
        onAcknowledge={handleLimitationsAcknowledge}
        onClose={() => setShowLimitationsModal(false)}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/mhr"
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">Eligibility Review Results</h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-gray-600 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <User size={14} />
                Patient {review.patient_id?.slice(0, 8)}...
              </span>
              <span className="flex items-center gap-1">
                <ClipboardList size={14} />
                Protocol
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowLimitationsModal(true)}
            className="px-3 py-2 text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors min-h-[44px] sm:min-h-0"
          >
            View Limitations
          </button>
          {isCancellable && (
            <button
              onClick={() => setShowCancelModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors min-h-[44px] sm:min-h-0"
            >
              <XCircle size={18} />
              Cancel Review
            </button>
          )}
          {isCompleted && (
            <button
              onClick={() => navigate(`/mhr/multi-study?sourceReviewId=${reviewId}&patientId=${review.patient_id}`)}
              className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors min-h-[44px] sm:min-h-0"
            >
              <ClipboardList size={18} />
              Consider for Other Studies
            </button>
          )}
          <button
            onClick={handleExport}
            disabled={isExporting || !isCompleted}
            className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors min-h-[44px] sm:min-h-0"
          >
            {isExporting ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Download size={18} />
            )}
            Export
          </button>
        </div>
      </div>

      {/* Overall Result Banner */}
      <OverallResultBanner
        summary={review.summary}
        status={review.status}
        overallResult={review.overall_result}
      />

      {/* Quick Jump Bar (pages with findings) */}
      {pagesWithFindings.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap bg-gray-50 dark:bg-gray-900/40 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
            <FileText size={12} />
            Quick jump:
          </span>
          <div className="flex items-center gap-1 flex-wrap">
            {pagesWithFindings.slice(0, 15).map(page => (
              <button
                key={page}
                onClick={() => {
                  setCurrentPage(page);
                  if (isMobile) setActiveTab('document');
                }}
                className={`min-w-[28px] h-7 px-2 text-xs font-medium rounded transition-colors ${
                  page === currentPage
                    ? 'bg-primary-600 text-white'
                    : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/40 border border-amber-300 dark:border-amber-700'
                }`}
              >
                {page}
              </button>
            ))}
            {pagesWithFindings.length > 15 && (
              <span className="text-xs text-gray-400 dark:text-gray-500">
                +{pagesWithFindings.length - 15} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Cost Summary (collapsible) */}
      <CostSummary
        totalCost={review.cost?.total_cost || 0}
        cacheSavings={review.cost?.cache_savings || 0}
      />

      {/* Mobile Tab Navigation */}
      {isMobile && (
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('results')}
            className={`flex-1 py-3 text-sm font-medium text-center transition-colors ${
              activeTab === 'results'
                ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <ListFilter size={16} className="inline mr-2" />
            Results
          </button>
          <button
            onClick={() => setActiveTab('document')}
            className={`flex-1 py-3 text-sm font-medium text-center transition-colors ${
              activeTab === 'document'
                ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <FileText size={16} className="inline mr-2" />
            Document
          </button>
        </div>
      )}

      {/* Main Content - Split Layout on Desktop */}
      <div className="flex flex-col md:flex-row gap-6">
        {/* Results Panel */}
        <div
          className={`${
            isMobile && activeTab !== 'results' ? 'hidden' : ''
          } md:w-2/5 space-y-4`}
        >
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-700 dark:text-green-300">{review.summary.met}</div>
              <div className="text-xs text-green-600 dark:text-green-400">Met</div>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-red-700 dark:text-red-300">{review.summary.not_met}</div>
              <div className="text-xs text-red-600 dark:text-red-400">Not Met</div>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                {review.summary.uncertain + review.summary.not_found}
              </div>
              <div className="text-xs text-amber-600 dark:text-amber-400">Review Needed</div>
            </div>
          </div>

          {/* Exclusion Criteria Section (show first if any are met) */}
          {exclusionCriteria.length > 0 && (
            <CollapsibleSection
              title="Exclusion Criteria"
              icon={AlertCircle}
              count={exclusionCriteria.length}
              defaultOpen={exclusionsMetCount > 0}
              iconBgColor="bg-orange-100 dark:bg-orange-900/40"
              iconColor="text-orange-600 dark:text-orange-400"
              priority={exclusionsMetCount > 0}
            >
              {exclusionCriteria.map(result => (
                <div key={result.id} className="space-y-2">
                  <CriteriaResultCard
                    result={result}
                    onCitationClick={handleCitationClick}
                    onVerify={() => handleVerify(result)}
                    isExpanded={expandedCriteriaId === result.id}
                    onToggleExpand={() =>
                      setExpandedCriteriaId(
                        expandedCriteriaId === result.id ? null : result.id
                      )
                    }
                  />
                  {expandedCriteriaId === result.id && !result.human_verified && (
                    <div className="ml-4 border-l-2 border-primary-200 dark:border-primary-800 pl-4">
                      <CriterionVerification
                        result={result}
                        onVerify={payload => handleVerificationSubmit(result.id, payload)}
                        disabled={isCompleted}
                      />
                    </div>
                  )}
                </div>
              ))}
            </CollapsibleSection>
          )}

          {/* Inclusion Criteria Section */}
          <CollapsibleSection
            title="Inclusion Criteria"
            icon={ClipboardList}
            count={inclusionCriteria.length}
            defaultOpen={true}
            iconBgColor="bg-blue-100 dark:bg-blue-900/40"
            iconColor="text-blue-600 dark:text-blue-400"
          >
            {inclusionCriteria.map(result => (
              <div key={result.id} className="space-y-2">
                <CriteriaResultCard
                  result={result}
                  onCitationClick={handleCitationClick}
                  onVerify={() => handleVerify(result)}
                  isExpanded={expandedCriteriaId === result.id}
                  onToggleExpand={() =>
                    setExpandedCriteriaId(
                      expandedCriteriaId === result.id ? null : result.id
                    )
                  }
                />
                {expandedCriteriaId === result.id && !result.human_verified && (
                  <div className="ml-4 border-l-2 border-primary-200 dark:border-primary-800 pl-4">
                    <CriterionVerification
                      result={result}
                      onVerify={payload => handleVerificationSubmit(result.id, payload)}
                      disabled={isCompleted}
                    />
                  </div>
                )}
              </div>
            ))}
          </CollapsibleSection>

          {/* Review Sign-Off Section */}
          <ReviewSignOff
            reviewId={review.review_id}
            onComplete={handleComplete}
            isCompleted={isCompleted}
            completedAt={review.completed_at}
          />
        </div>

        {/* Document Viewer Panel */}
        <div
          className={`${
            isMobile && activeTab !== 'document' ? 'hidden' : ''
          } md:w-3/5 space-y-4`}
        >
          {/* Document Controls */}
          <div className="flex items-center justify-between gap-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
            <PageNavigation
              currentPage={currentPage}
              totalPages={totalPages || review.document_coverage?.total_pages || 1}
              onPageChange={setCurrentPage}
              pagesWithFindings={pagesWithFindings}
            />
            <ZoomControls zoom={zoom} onZoomChange={setZoom} />
          </div>

          {/* PDF Viewer */}
          <div
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden relative"
            style={{ height: isMobile ? '60vh' : '70vh' }}
          >
            {pdfUrl ? (
              <PDFViewer
                documentUrl={pdfUrl}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                onLoadSuccess={handleLoadSuccess}
                zoom={zoom}
              />
            ) : (
              <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-gray-900/40">
                <p className="text-gray-500 dark:text-gray-400">No document available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Excerpt Panel (slide-in) */}
      <ExcerptPanel
        citation={selectedCitation}
        onClose={handleCloseExcerptPanel}
        onGoToPage={handleGoToPage}
      />

      {/* Polling Indicator */}
      {isPolling && !pollingError && (
        <div className="fixed bottom-4 right-4 bg-primary-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <Loader2 size={16} className="animate-spin" />
          <span className="text-sm">Analysis in progress...</span>
        </div>
      )}

      {/* Polling Error Banner */}
      {pollingError && (
        <div className="fixed bottom-4 right-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-3 rounded-lg shadow-lg max-w-sm">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="flex-shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
            <div className="flex-1">
              <p className="text-sm font-medium">Connection Issue</p>
              <p className="text-xs mt-1 text-red-700 dark:text-red-300">{pollingError}</p>
              <button
                onClick={retryPolling}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors min-h-[44px] sm:min-h-0"
              >
                <RefreshCw size={12} />
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancelled Banner */}
      {isCancelled && (
        <div className="fixed bottom-4 right-4 bg-amber-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <XCircle size={16} />
          <span className="text-sm">Review was cancelled</span>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      <CancelConfirmationModal
        isOpen={showCancelModal}
        onConfirm={handleCancelConfirm}
        onCancel={() => setShowCancelModal(false)}
        isCancelling={isCancelling}
      />

      {/* Success Toast */}
      <Toast message={success} type="success" onClose={clearSuccess} />

      {/* Error Toast (e.g. failed verification) */}
      <Toast message={errorMsg} type="error" onClose={clearError} duration={6000} />
    </div>
  );
}

export default ReviewResults;
